// src/ChatApp.js
// useState 훅을 import 합니다.
import React, { useState } from 'react';
import axios from 'axios';
import ChatDisplay from './components/ChatDisplay';
import MessageForm from './components/MessageForm';

/*
  [ChatApp.js 설명]
  - 고정 폭/높이(375x812) 컨테이너로 "휴대폰 화면"을 흉내냅니다.
  - 대화 내역을 messages 상태로 관리합니다.
  - "톡센세에게 물어봐!" 버튼 클릭 시 handleAskSensei 함수가 실행되어
     • 로딩 상태(isLoading)를 true로 설정하고,
     • 백엔드로 conversation 배열 전송 후,
     • 생성된 추천 답변을 누적 저장하며,
     • 완료 후 isLoading을 false로 전환합니다.
  - 추가된 기능:
     1. 로딩 중에도 기존 추천 답변들을 계속 표시합니다.
     2. 각 추천 답변에 우측 상단에 'x' 버튼을 배치하여 삭제할 수 있도록 합니다.
     3. 헤더에 버전 정보(ver.0)와 안내(Readme) 모달 버튼 추가
     4. 전반적인 폰트 크기 조정 (헤더 등)
*/

function ChatApp() {
  // 대화 내역
  const [messages, setMessages] = useState([
    { id: 1, sender: "내 메시지", text: "이번에 나온 장원영 솔로 앨범 봤어? 너무 예쁘지? ㅎㅎ" },
    { id: 2, sender: "상대방 메시지", text: "그럴거면 장원영이랑 사귀지 그래?" },
  ]);

  // AI로부터 받은 추천 답변들을 배열로 관리
  const [recommendations, setRecommendations] = useState([]);

  // 로딩 상태
  const [isLoading, setIsLoading] = useState(false);

  // --- 추가: Readme 모달 상태 ---
  const [isReadmeOpen, setIsReadmeOpen] = useState(false);
  // --------------------------

  // 메시지 추가 함수
  const addMessage = (sender, text) => {
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), sender, text }
    ]);
  };

  // 메시지 수정/삭제 함수
  const updateMessage = (id, newText) => {
    setMessages((prev) =>
      prev.map((msg) => (msg.id === id ? { ...msg, text: newText } : msg))
    );
  };
  const deleteMessage = (id) => {
    setMessages((prev) => prev.filter((msg) => msg.id !== id));
  };

  // 추천 답변 삭제 함수
  const deleteRecommendation = (indexToDelete) => {
    setRecommendations((prevRecommendations) =>
      prevRecommendations.filter((_, index) => index !== indexToDelete)
    );
  };

  // "톡센세에게 물어봐!" 버튼 클릭 핸들러
  const handleAskSensei = async () => {
    try {
      setIsLoading(true);
      const conversation = messages.map((msg) => {
        if (msg.sender === "상대방 메시지") {
          return {
            role: "user",
            content: [{ type: "text", text: msg.text }]
          };
        } else if (msg.sender === "내 메시지") {
          return {
            role: "assistant",
            content: [{ type: "text", text: msg.text }]
          };
        } else {
          return {
            role: "user",
            content: [{ type: "text", text: msg.text }]
          };
        }
      });

      const baseURL = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const res = await axios.post(`${baseURL}/api/generate`, { conversation });

      setRecommendations((prev) => [...prev, res.data.response]);
    } catch (error) {
      console.error("API 호출 중 오류:", error);
      if (error.response && error.response.status === 429) {
        setRecommendations((prev) => [...prev, "아직 가난한 대학생 이랍니다... 내일 다시 이용해 주세요ㅠㅠ"]);
      } else {
        const errorMessage = error.response?.data?.error || "추천 답변을 가져오는 중 오류가 발생했습니다.";
        setRecommendations((prev) => [...prev, errorMessage]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // --- 추가: Readme 모달 토글 함수 ---
  const toggleReadme = () => {
    setIsReadmeOpen(!isReadmeOpen);
  };
  // ---------------------------------

  return (
    <div className="bg-gray-100 min-h-screen flex justify-center items-center font-sans"> {/* 기본 폰트 적용 */}
      <div className="w-[375px] h-[812px] bg-white flex flex-col overflow-hidden shadow-lg rounded-lg border border-gray-200"> {/* 그림자, 라운드, 테두리 추가 */}
        {/* 헤더 수정 */}
        <header className="p-4 bg-blue-500 text-white flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-baseline space-x-2">
            {/* 폰트 크기 조정 (text-xl) */}
            <h1 className="text-xl font-bold">TalkSensei</h1>
            {/* 버전 정보 추가 (text-xs) */}
            <span className="text-xs font-light">ver.0</span>
          </div>
          {/* 안내 버튼 추가 */}
          <button
            onClick={toggleReadme}
            className="text-sm bg-white text-blue-500 px-2 py-1 rounded hover:bg-blue-100 transition duration-200"
          >
            ReadMe
          </button>
        </header>

        {/* 메인 컨텐츠 영역 (스크롤 가능) */}
        <main className="p-4 space-y-4 flex-grow overflow-y-auto">
          {/* ChatDisplay 폰트 크기는 기본(text-base) 또는 ChatMessage 내부에서 text-sm 사용 */}
          <ChatDisplay
            messages={messages}
            onUpdateMessage={updateMessage}
            onDeleteMessage={deleteMessage}
          />
          {/* MessageForm 내부 폰트 크기는 기본(text-base) 또는 text-sm */}
          <MessageForm onAddMessage={addMessage} />
          <div className="p-4 bg-gray-50 rounded shadow">
            <button
              onClick={handleAskSensei}
              // 버튼 폰트 크기 기본(text-base) 또는 text-sm
              className="w-full p-2 bg-green-500 text-white rounded hover:bg-green-600 transition duration-200 disabled:opacity-50"
              disabled={isLoading}
            >
              {isLoading ? '머리굴리는 중...' : '톡센세에게 물어봐!'}
            </button>
            <div className="mt-4 space-y-4">
              {recommendations.map((rec, index) => (
                <div
                  key={index}
                  className="relative p-3 border border-gray-300 rounded bg-white shadow-sm" // 패딩 증가, 그림자 추가
                >
                  <button
                    onClick={() => deleteRecommendation(index)}
                    className="absolute top-1 right-1 text-red-400 hover:text-red-600 font-bold px-1 text-lg" // 크기 및 색상 조정
                    title="삭제"
                  >
                    &times; {/* HTML entity for 'x' */}
                  </button>
                  <p className="font-semibold mb-1 text-sm text-gray-700">톡센세 추천 답변:</p> {/* 폰트 크기/색상 조정 */}
                  {/* 추천 답변 폰트 크기 기본(text-base) 또는 text-sm */}
                  <p className="whitespace-pre-wrap break-words text-sm">{rec}</p>
                </div>
              ))}
              {isLoading && (
                <div className="flex items-center justify-center space-x-2 text-green-600 mt-2"> {/* 가운데 정렬 */}
                  {/* 로딩 텍스트 폰트 크기 text-sm */}
                  <span className="text-sm">머리굴리는 중</span>
                  <svg
                    className="animate-spin h-4 w-4 text-green-500" // 크기 조정
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v8H4z"
                    />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* --- 추가: Readme 모달 --- */}
      {isReadmeOpen && (
        // 모달 배경 (어둡게 처리하고 클릭 시 닫기)
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
          onClick={toggleReadme} // 배경 클릭 시 닫기
        >
          {/* 모달 컨텐츠 (배경 클릭 이벤트 전파 방지) */}
          <div
            className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full relative"
            onClick={(e) => e.stopPropagation()} // 모달 내부 클릭 시 닫히지 않도록 함
          >
            {/* 닫기 버튼 */}
            <button
              onClick={toggleReadme}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-2xl font-bold"
              title="닫기"
            >
              &times;
            </button>
            {/* 모달 제목 */}
            <h2 className="text-lg font-bold mb-4 text-blue-600">톡센세가 뭔데??</h2>
            {/* 모달 내용 */}
            <div className="text-sm text-gray-700 space-y-2">
              <p>
                톡센세는 연애경험 부족하거나 이성과의 sns 대화에 어려움을 겪는 사람들을 위한 &lt;이성간의 sns 답변추천 서비스&gt; 입니다.
              </p>
              <p>
                아직 초기 단계 이기도 하고 이런 종류의 서비스 개발도 처음이기에 부족한게 많아요ㅠㅠㅠ
              </p>
              <p>
                사용에 제한 없는 완전 무료 서비스 중이니 부디 톡센세가 도움이 되어 많은 커플이 생기길 바라겠습니다!
              </p>
              <p className="mt-2 text-xs text-gray-500">
                (ver.0은 남성연애 고수의 내역을 학습시킨 모델입니다. 여성분들은 조금만 기다려 주세요...)
              </p>
              <hr className="my-3"/> {/* 구분선 */}
              <p className="font-semibold">PS.</p>
              <p>
                채팅내역을 실제 카톡과 같은 sns 내역과 똑같이 만들고 '톡센세에게 물어봐!' 버튼을 누르면 '나' 입장의 추천답변이 생길거에요! 부디 실제 대화에 직접 사용하지 말고 아이디어를 얻는 용도로 사용해 주세요ㅜㅜ
              </p>
            </div>
          </div>
        </div>
      )}
      {/* ----------------------- */}
    </div>
  );
}

export default ChatApp;
